import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { usersAPI } from '../services/api';
import { Capacitor } from '@capacitor/core';

const AuthContext = createContext(null);

const safeJsonParse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const isNative = Capacitor.isNativePlatform();
  const [user, setUser] = useState(() => {
    if (isNative) return null;
    return safeJsonParse(localStorage.getItem('user'));
  });

  const [token, setToken] = useState(() => (isNative ? null : localStorage.getItem('token')));
  const [permissions, setPermissions] = useState(() => {
    if (isNative) return null;
    return safeJsonParse(localStorage.getItem('permissions'));
  });
  const [permissionsReady, setPermissionsReady] = useState(() => (isNative ? true : !!localStorage.getItem('permissions')));

  useEffect(() => {
    if (!isNative) return;
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('permissions');
    setUser(null);
    setToken(null);
    setPermissions(null);
    setPermissionsReady(true);
  }, [isNative]);

  const login = useCallback((userData, authToken) => {
    const resolvedPermissions = userData.permissions || null;

    setUser(userData);
    setToken(authToken);
    setPermissions(resolvedPermissions);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', authToken);
    if (resolvedPermissions) {
      localStorage.setItem('permissions', JSON.stringify(resolvedPermissions));
      setPermissionsReady(true);
    } else {
      localStorage.removeItem('permissions');
      setPermissionsReady(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setPermissions(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('permissions');
    setPermissionsReady(false);
  }, []);

  const refreshPermissions = useCallback(async () => {
    if (!token) return;

    try {
      const response = await usersAPI.getMyPermissions();
      const nextPermissions = response.data.permissions;
      setPermissions(nextPermissions);
      localStorage.setItem('permissions', JSON.stringify(nextPermissions));
      setPermissionsReady(true);
    } catch (error) {
      // Keep app usable if permission refresh fails.
      setPermissionsReady(true);
    }
  }, [token]);

  const can = useCallback(
    (moduleName, actionName) => {
      if (!user) return false;
      if (user.role === 'admin' && !permissions) return true;
      return !!permissions?.[moduleName]?.[actionName];
    },
    [permissions, user]
  );

  useEffect(() => {
    if (!token || !user) return;
    refreshPermissions();

    const intervalId = setInterval(() => {
      refreshPermissions();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [token, user, refreshPermissions]);

  useEffect(() => {
    const onFocus = () => {
      if (token && user) {
        refreshPermissions();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [token, user, refreshPermissions]);

  return (
    <AuthContext.Provider value={{ user, token, permissions, permissionsReady, login, logout, can, refreshPermissions }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
